import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BookOpen, 
  Building2, 
  Users, 
  Calendar, 
  Clock, 
  FileText, 
  Wallet, 
  BarChart3, 
  Settings, 
  HelpCircle,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  ArrowUp,
  Mail,
  Download,
  KeyRound,
  Shield,
  Award,
  FileUp,
  TrendingUp,
  Euro,
  Heart,
  Package,
  Crown,
  Star,
  Bell,
  Landmark,
  AlertTriangle,
  Car
} from 'lucide-react';

// ============= PIANO GRATUITO =============
const chaptersGratuito = [
  {
    id: 1,
    title: "Introduzione alla piattaforma",
    icon: BookOpen,
    content: `
      <p>Questa piattaforma è uno strumento pensato per aiutare ETS, APS, ODV, Associazioni e Fondazioni a gestire in modo semplice:</p>
      <ul>
        <li>volontari</li>
        <li>attività</li>
        <li>documenti</li>
        <li>rendicontazioni</li>
        <li>report</li>
      </ul>
      <p><strong>L'obiettivo della piattaforma</strong> è semplificare la gestione quotidiana dell'ente e ridurre il tempo dedicato alle attività amministrative.</p>
      <p>Con il <strong>Piano Gratuito</strong> puoi già accedere a molte funzionalità essenziali per gestire la tua associazione.</p>
      
      <h4>Limiti del Piano Gratuito:</h4>
      <ul>
        <li><strong>20 volontari attivi</strong></li>
        <li><strong>30 soggetti unici</strong></li>
        <li>10 eventi/anno</li>
        <li>50 MB di storage</li>
      </ul>
      <p><em>Nota: Il passaggio al piano successivo avviene se superi anche uno solo dei due limiti (volontari attivi o soggetti unici).</em></p>
      
      <h4>Cosa NON include il Piano Free:</h4>
      <ul>
        <li>Comunicazioni Email/WhatsApp (Premium+)</li>
        <li>Archivio Documenti e Certificati (Premium+)</li>
        <li>Modulo Governance - Organi, Assemblee, Consiglio (Governance+)</li>
        <li>Rendicontazione Economica (Governance+)</li>
        <li>Magazzino e Automezzi (Pro+)</li>
        <li>Statistiche Avanzate (Pro+)</li>
      </ul>
    `
  },
  {
    id: 2,
    title: "Registrazione e configurazione dell'Ente",
    icon: Building2,
    content: `
      <p>Quando un ente si registra sulla piattaforma è necessario inserire alcune informazioni di base.</p>
      
      <h4>Inserire i dati dell'ente</h4>
      <ol>
        <li>Aprire la sezione <strong>Impostazioni Ente</strong></li>
        <li>Inserire i dati principali dell'organizzazione</li>
        <li>Salvare le informazioni</li>
      </ol>
      
      <p>I dati richiesti includono:</p>
      <ul>
        <li>nome dell'ente</li>
        <li>codice fiscale</li>
        <li>partita IVA (se presente)</li>
        <li>indirizzo della sede</li>
        <li>email e telefono</li>
      </ul>
      
      <h4>Caricare il logo</h4>
      <p>Per rendere i documenti più professionali è possibile caricare il logo dell'ente.</p>
      <ol>
        <li>Aprire <strong>Impostazioni Ente</strong></li>
        <li>Selezionare <strong>Carica logo</strong></li>
        <li>Scegliere un file in formato JPG o PNG (massimo 500KB)</li>
        <li>Salvare</li>
      </ol>
    `
  },
  {
    id: 3,
    title: "Gestione dei Volontari",
    icon: Users,
    content: `
      <p>La piattaforma permette di creare un registro digitale dei volontari.</p>
      
      <h4>Inserire un nuovo volontario</h4>
      <ol>
        <li>Aprire la sezione <strong>Volontari</strong></li>
        <li>Cliccare su <strong>Nuovo Volontario</strong></li>
        <li>Inserire i dati richiesti</li>
        <li>Salvare</li>
      </ol>
      
      <p>I dati principali sono:</p>
      <ul>
        <li>nome e cognome</li>
        <li>codice fiscale</li>
        <li>data e luogo di nascita</li>
        <li>contatti (telefono, email)</li>
        <li>documento di identità</li>
      </ul>
      
      <h4>Volontari occasionali e non occasionali</h4>
      <p>Durante l'inserimento è possibile indicare se il volontario è:</p>
      <ul>
        <li><strong>Volontario non occasionale</strong> - collabora regolarmente con l'ente</li>
        <li><strong>Volontario occasionale</strong> - partecipa saltuariamente alle attività</li>
      </ul>
    `
  },
  {
    id: 4,
    title: "Gestione delle Attività e degli Eventi",
    icon: Calendar,
    content: `
      <p>Gli enti possono registrare tutte le attività svolte con i volontari.</p>
      
      <h4>Creare una nuova attività</h4>
      <ol>
        <li>Aprire la sezione <strong>Eventi</strong></li>
        <li>Cliccare su <strong>Nuovo Evento</strong></li>
        <li>Inserire nome, data, luogo e descrizione</li>
        <li>Salvare</li>
      </ol>
      
      <h4>Visualizzare le attività</h4>
      <p>Nella sezione <strong>Eventi</strong> è possibile vedere tutte le attività passate, in corso e programmate.</p>
    `
  },
  {
    id: 5,
    title: "Presenze e Ore di Volontariato",
    icon: Clock,
    content: `
      <p>La piattaforma consente di registrare le ore di volontariato svolte.</p>
      
      <h4>Registrare le presenze</h4>
      <ol>
        <li>Aprire la sezione <strong>Presenze</strong></li>
        <li>Selezionare l'evento</li>
        <li>Aggiungere i volontari partecipanti</li>
        <li>Inserire ora di inizio e fine</li>
        <li>Salvare</li>
      </ol>
      
      <h4>Check-in rapido</h4>
      <p>Per una registrazione veloce delle presenze:</p>
      <ol>
        <li>Aprire la sezione <strong>Check-in Rapido</strong></li>
        <li>Selezionare l'evento del giorno</li>
        <li>Spuntare i volontari presenti</li>
        <li>Confermare</li>
      </ol>
      
      <h4>Visualizzare le ore</h4>
      <p>Nella sezione <strong>Ore Volontari</strong> è possibile vedere le ore totali di ogni volontario e lo storico delle attività.</p>
    `
  },
  {
    id: 6,
    title: "Kit Amministrativo ETS",
    icon: FileText,
    content: `
      <p>Il Kit Amministrativo ETS è <strong>gratuito per tutti i piani</strong> e contiene modelli pronti all'uso per la gestione dei volontari.</p>
      
      <p><em>Nota: Questa funzione si trova nel menu <strong>VOLONTARI → Kit Amministrativo ETS</strong>.</em></p>
      
      <h4>Documenti disponibili:</h4>
      <ul>
        <li>Informativa privacy</li>
        <li>Consenso trattamento dati</li>
        <li>Regolamento volontari</li>
        <li>Modulo iscrizione</li>
        <li>Scheda volontario</li>
        <li>Dichiarazione assicurativa</li>
        <li>Liberatoria immagini</li>
      </ul>
      
      <h4>Come scaricare i modelli</h4>
      <ol>
        <li>Aprire il menu <strong>VOLONTARI</strong></li>
        <li>Selezionare <strong>Kit Amministrativo ETS</strong></li>
        <li>Scegliere il documento desiderato</li>
        <li>Cliccare su <strong>Scarica PDF</strong></li>
      </ol>
      <p>I modelli sono già precompilati con i dati dell'ente.</p>
    `
  },
  {
    id: 7,
    title: "Report Base",
    icon: BarChart3,
    content: `
      <p>La sezione Report è <strong>gratuita per tutti i piani</strong> e permette di generare report automatici.</p>
      
      <h4>I report possono includere:</h4>
      <ul>
        <li>numero di volontari attivi</li>
        <li>ore di volontariato svolte</li>
        <li>attività realizzate</li>
        <li>statistiche sull'impegno dei volontari</li>
      </ul>
      
      <h4>Generare un report</h4>
      <ol>
        <li>Aprire la sezione <strong>Report</strong></li>
        <li>Scegliere il tipo di report desiderato</li>
        <li>Selezionare il periodo</li>
        <li>Cliccare su <strong>Genera</strong></li>
      </ol>
    `
  },
  {
    id: 8,
    title: "Impostazioni e Profilo",
    icon: Settings,
    content: `
      <p>Nella sezione <strong>Impostazioni</strong> è possibile gestire:</p>
      <ul>
        <li>dati dell'ente</li>
        <li>logo</li>
        <li>piano di abbonamento</li>
      </ul>
      
      <h4>Modificare i dati dell'ente</h4>
      <ol>
        <li>Aprire <strong>Impostazioni Ente</strong></li>
        <li>Modificare i campi desiderati</li>
        <li>Cliccare su <strong>Salva Modifiche</strong></li>
      </ol>
      
      <h4>Visualizzare il piano attivo</h4>
      <ol>
        <li>Aprire <strong>Piani e Prezzi</strong></li>
        <li>Visualizzare il piano attuale e i limiti</li>
        <li>Se necessario, effettuare l'upgrade</li>
      </ol>
    `
  },
  {
    id: 9,
    title: "Registrazione, Email e Password",
    icon: Mail,
    content: `
      <h4>Verifica Email</h4>
      <p>Per garantire la sicurezza, Assietica Gest richiede la verifica dell'email durante la registrazione.</p>
      <ol>
        <li>Compilare il modulo di registrazione</li>
        <li>Controllare la casella email (anche spam)</li>
        <li>Cliccare sul link di verifica</li>
      </ol>
      
      <h4>Reset Password</h4>
      <p>Se hai dimenticato la password:</p>
      <ol>
        <li>Dalla pagina di login, clicca su <strong>Password dimenticata?</strong></li>
        <li>Inserisci l'email associata al tuo account</li>
        <li>Se l'email corrisponde ad un utente registrato, riceverai un link per reimpostare la password</li>
        <li>Clicca sul link ricevuto nell'email (valido per 1 ora)</li>
        <li>Inserisci la nuova password (minimo 8 caratteri)</li>
      </ol>
      <p><em>Nota: le email vengono inviate da info@assietica.it. Controlla anche la cartella spam.</em></p>

      <h4>Navigazione Lista Soci</h4>
      <p>La lista soci supporta la <strong>paginazione</strong>: vengono mostrati 50 soci per pagina. Usa i pulsanti "Precedente" e "Successiva" in fondo alla tabella per navigare tra le pagine. Il contatore in basso mostra la pagina corrente e il totale dei soci.</p>
      <p>Puoi anche usare i filtri (ricerca per nome, tipo socio, stato) per trovare rapidamente un socio specifico.</p>
    `
  },
  {
    id: 10,
    title: "Backup e Scarico Dati",
    icon: Download,
    content: `
      <p>Assietica Gest ti permette di <strong>scaricare una copia completa</strong> di tutti i dati della tua organizzazione in qualsiasi momento.</p>
      
      <h4>Perché è importante</h4>
      <p>Il backup ti garantisce:</p>
      <ul>
        <li>Sicurezza dei tuoi dati in caso di problemi</li>
        <li>Portabilità: puoi esportare i dati se decidi di cambiare piattaforma</li>
        <li>Conformità GDPR: hai sempre accesso ai tuoi dati</li>
      </ul>
      
      <h4>Cosa puoi scaricare</h4>
      <ul>
        <li><strong>Volontari</strong> - Anagrafica completa in CSV</li>
        <li><strong>Soci e Persone</strong> - Registro soci in CSV</li>
        <li><strong>Eventi</strong> - Elenco attività in CSV</li>
        <li><strong>Presenze</strong> - Storico presenze in CSV</li>
        <li><strong>Quote Associative</strong> - Pagamenti soci in CSV</li>
        <li><strong>Entrate e Uscite</strong> - Contabilità in CSV</li>
        <li><strong>Documenti</strong> - Tutti i file caricati (PDF, immagini, ecc.)</li>
      </ul>
      
      <h4>Come scaricare i dati</h4>
      <ol>
        <li>Vai su <strong>Impostazioni > Esporta i Tuoi Dati</strong></li>
        <li>Visualizza l'anteprima dei dati disponibili</li>
        <li>Scegli tra:
          <ul>
            <li><strong>Export Completo (ZIP)</strong> - Un unico archivio con tutti i dati e documenti</li>
            <li><strong>Export Singoli (CSV)</strong> - Scarica singole tabelle per Excel</li>
          </ul>
        </li>
        <li>Clicca su <strong>Scarica</strong></li>
      </ol>
      
      <h4>Formato dei file</h4>
      <ul>
        <li><strong>CSV</strong> - Compatibile con Excel, LibreOffice, Google Sheets</li>
        <li><strong>ZIP</strong> - Archivio compresso con cartelle organizzate per categoria</li>
      </ul>
      
      <p><em>Nota: L'export dei dati è sempre disponibile, anche se l'abbonamento è scaduto.</em></p>
    `
  },
  {
    id: 11,
    title: "Cancellazione Account",
    icon: AlertTriangle,
    content: `
      <p>Se desideri chiudere il tuo account, Assietica Gest ti permette di richiedere la <strong>cancellazione volontaria</strong> in modo semplice e sicuro.</p>
      
      <h4>Periodo di Grazia: 30 giorni</h4>
      <p>Dopo aver richiesto la cancellazione, avrai <strong>30 giorni</strong> per ripensarci. Durante questo periodo:</p>
      <ul>
        <li>Puoi continuare ad accedere al tuo account normalmente</li>
        <li>Puoi scaricare un backup completo dei tuoi dati</li>
        <li>Puoi annullare la cancellazione in qualsiasi momento</li>
      </ul>
      
      <h4>Come richiedere la cancellazione</h4>
      <ol>
        <li>Vai su <strong>Impostazioni - Impostazioni Ente</strong></li>
        <li>Scorri fino alla sezione <strong>Cancellazione Account</strong> (in fondo alla pagina)</li>
        <li>Clicca su <strong>Richiedi Cancellazione</strong></li>
        <li><strong>Step 1:</strong> Conferma l'intenzione di procedere</li>
        <li><strong>Step 2:</strong> Seleziona un motivo (ci aiuta a migliorare) e scarica il backup</li>
        <li><strong>Step 3:</strong> Spunta la casella di conferma e clicca <strong>Conferma Cancellazione</strong></li>
      </ol>
      
      <h4>Annullare la cancellazione</h4>
      <p>Se hai cambiato idea durante i 30 giorni di grazia:</p>
      <ol>
        <li>Vai su <strong>Impostazioni - Impostazioni Ente</strong></li>
        <li>Nella sezione Cancellazione Account vedrai lo stato "In cancellazione"</li>
        <li>Clicca su <strong>Annulla Cancellazione</strong></li>
        <li>Il tuo account tornera immediatamente attivo</li>
      </ol>
      
      <h4>Cosa succede dopo i 30 giorni</h4>
      <ul>
        <li>L'accesso al tuo account verra disabilitato</li>
        <li>I dati verranno conservati per 6 mesi (recovery window)</li>
        <li>Dopo 24 mesi dalla cessazione, i dati verranno anonimizzati (GDPR)</li>
      </ul>
      
      <h4>Importante</h4>
      <p><em>Ti consigliamo sempre di scaricare un backup completo dei tuoi dati prima di confermare la cancellazione. Puoi farlo dalla sezione Esporta i Tuoi Dati.</em></p>
    `
  },
  {
    id: 12,
    title: "FAQ Piano Gratuito",
    icon: HelpCircle,
    content: `
      <h4>Quali funzionalità sono incluse nel piano gratuito?</h4>
      <p>Il piano gratuito include: gestione volontari, eventi, presenze, check-in rapido, kit amministrativo, report base, backup dati e impostazioni ente.</p>
      
      <h4>Quanti volontari posso gestire?</h4>
      <p>Con il piano gratuito puoi gestire fino a <strong>20 volontari attivi</strong> e <strong>30 soggetti unici</strong>.</p>
      
      <h4>Cosa succede se supero i limiti?</h4>
      <p>Se superi anche uno solo dei due limiti (volontari attivi o soggetti unici), riceverai un avviso per passare al piano Premium (€9,99/mese IVA inclusa).</p>
      
      <h4>Posso fare l'upgrade in qualsiasi momento?</h4>
      <p>Sì, puoi passare a Premium, Governance, Pro o Enterprise in qualsiasi momento dalla sezione Piani e Prezzi.</p>
      
      <h4>I miei dati sono al sicuro?</h4>
      <p>Sì, tutti i dati sono protetti e conservati in modo sicuro secondo le normative GDPR.</p>
      
      <h4>Posso scaricare i miei dati?</h4>
      <p>Sì, puoi sempre esportare tutti i tuoi dati dalla sezione Impostazioni > Esporta i Tuoi Dati.</p>
    `
  },
  {
    id: 13,
    title: "Dashboard - La tua panoramica",
    icon: BookOpen,
    content: `
      <p>La <strong>Dashboard</strong> è la pagina principale che vedi dopo il login. Ti offre una panoramica completa dell'ente.</p>
      
      <h4>Cosa trovi nella Dashboard:</h4>
      <ul>
        <li><strong>Statistiche rapide</strong> - Numero volontari attivi, eventi del mese, ore totali</li>
        <li><strong>Alert scadenze</strong> - Documenti e coperture in scadenza</li>
        <li><strong>Azioni rapide</strong> - Pulsanti per le operazioni più comuni</li>
        <li><strong>Attività recenti</strong> - Ultime operazioni effettuate</li>
      </ul>
      
      <h4>Azioni Rapide</h4>
      <p>I pulsanti delle Azioni Rapide ti permettono di accedere velocemente alle funzioni più utilizzate:</p>
      
      <table style="width:100%; border-collapse: collapse; margin: 10px 0;">
        <tr style="background:#f1f5f9;">
          <th style="padding:8px; text-align:left; border:1px solid #e2e8f0;">Azione</th>
          <th style="padding:8px; text-align:left; border:1px solid #e2e8f0;">Piano Richiesto</th>
        </tr>
        <tr><td style="padding:8px; border:1px solid #e2e8f0;">Aggiungi Volontario</td><td style="padding:8px; border:1px solid #e2e8f0;">Free</td></tr>
        <tr><td style="padding:8px; border:1px solid #e2e8f0;">Crea Evento</td><td style="padding:8px; border:1px solid #e2e8f0;">Free</td></tr>
        <tr><td style="padding:8px; border:1px solid #e2e8f0;">Check-in Rapido</td><td style="padding:8px; border:1px solid #e2e8f0;">Free</td></tr>
        <tr><td style="padding:8px; border:1px solid #e2e8f0;">Esporta Registro Volontari</td><td style="padding:8px; border:1px solid #e2e8f0;">Free</td></tr>
        <tr><td style="padding:8px; border:1px solid #e2e8f0;">Aggiungi Socio</td><td style="padding:8px; border:1px solid #e2e8f0;"><strong>Premium+</strong></td></tr>
        <tr><td style="padding:8px; border:1px solid #e2e8f0;">Quote da Incassare</td><td style="padding:8px; border:1px solid #e2e8f0;"><strong>Premium+</strong></td></tr>
        <tr><td style="padding:8px; border:1px solid #e2e8f0;">Quote Incassate</td><td style="padding:8px; border:1px solid #e2e8f0;"><strong>Premium+</strong></td></tr>
        <tr><td style="padding:8px; border:1px solid #e2e8f0;">Esporta Registro Soci</td><td style="padding:8px; border:1px solid #e2e8f0;"><strong>Premium+</strong></td></tr>
        <tr><td style="padding:8px; border:1px solid #e2e8f0;">Registra Movimento Contabile</td><td style="padding:8px; border:1px solid #e2e8f0;"><strong>Governance+</strong></td></tr>
      </table>
      
      <h4>Quote da Incassare / Quote Incassate</h4>
      <p>Questi due pulsanti aprono un <strong>popup rapido</strong> direttamente dalla Dashboard:</p>
      <ul>
        <li><strong>Quote da Incassare:</strong> mostra tutti i soci con quota ancora da pagare per l'anno corrente. Puoi registrare il pagamento direttamente dal popup!</li>
        <li><strong>Quote Incassate:</strong> mostra l'elenco delle quote già pagate con data e importo.</li>
      </ul>
      <p>Entrambi i popup mostrano un riepilogo del totale in alto.</p>
      
      <p><em>Nota: Se un'azione non è disponibile per il tuo piano, vedrai un'icona lucchetto 🔒 sul pulsante. Cliccando, ti verrà suggerito di fare l'upgrade.</em></p>
      
      <h4>Widget Statistiche</h4>
      <p>I widget mostrano in tempo reale:</p>
      <ul>
        <li><strong>Volontari Attivi</strong> - numero e volontari da completare</li>
        <li><strong>Soci</strong> - totale soci registrati</li>
        <li><strong>Eventi Programmati</strong> - prossimi eventi e conclusi</li>
        <li><strong>Coperture in Scadenza</strong> - nei prossimi 30 giorni</li>
      </ul>
    `
  },
  {
    id: 13,
    title: "Coperture Assicurative",
    icon: Shield,
    content: `
      <p>La gestione delle coperture assicurative è fondamentale per proteggere i tuoi volontari.</p>
      
      <h4>Aggiungere una copertura</h4>
      <ol>
        <li>Aprire la sezione <strong>Coperture</strong></li>
        <li>Cliccare su <strong>Nuova Copertura</strong></li>
        <li>Selezionare il volontario</li>
        <li>Inserire i dati della polizza (compagnia, numero, date)</li>
        <li>Salvare</li>
      </ol>
      
      <h4>Dati della polizza:</h4>
      <ul>
        <li>Compagnia assicurativa</li>
        <li>Numero polizza</li>
        <li>Data inizio validità</li>
        <li>Data fine validità</li>
        <li>Tipo copertura (RC, Infortuni, ecc.)</li>
      </ul>
      
      <h4>Alert automatici</h4>
      <p>Il sistema ti avvisa automaticamente quando:</p>
      <ul>
        <li>Una copertura sta per scadere (30, 15, 7 giorni prima)</li>
        <li>Un volontario non ha copertura attiva</li>
        <li>Una polizza è già scaduta</li>
      </ul>
      
      <h4>Volontari senza copertura</h4>
      <p>Dalla sezione Coperture puoi vedere l'elenco dei volontari attivi senza una polizza valida.</p>
    `
  },
  {
    id: 14,
    title: "Sistema Notifiche",
    icon: Bell,
    content: `
      <p>Assietica Gest ti avvisa automaticamente per le scadenze importanti.</p>
      
      <h4>Tipi di notifiche:</h4>
      <ul>
        <li><strong>Documenti identità</strong> - Carte d'identità, patenti in scadenza</li>
        <li><strong>Coperture assicurative</strong> - Polizze in scadenza</li>
        <li><strong>Documenti compliance</strong> - Privacy, consensi da rinnovare</li>
      </ul>
      
      <h4>Priorità delle notifiche:</h4>
      <ul>
        <li><strong>Alta</strong> (rosso) - Scadenza entro 7 giorni</li>
        <li><strong>Media</strong> (arancione) - Scadenza entro 15 giorni</li>
        <li><strong>Bassa</strong> (giallo) - Scadenza entro 30 giorni</li>
      </ul>
      
      <h4>Gestire le notifiche</h4>
      <ol>
        <li>Clicca sull'icona campanella in alto</li>
        <li>Visualizza le notifiche pendenti</li>
        <li>Clicca su una notifica per i dettagli</li>
        <li>Segna come letta o archivia</li>
      </ol>
    `
  }
];

// ============= PIANO PREMIUM =============
const chaptersPremium = [
  {
    id: 1,
    title: "Introduzione al Piano Premium",
    icon: Crown,
    content: `
      <p>Il <strong>Piano Premium</strong> (€9,99/mese IVA inclusa) sblocca funzionalità avanzate per una gestione più completa dell'ente.</p>
      
      <h4>Limiti Piano Premium:</h4>
      <ul>
        <li><strong>60 volontari attivi</strong></li>
        <li><strong>100 soggetti unici</strong></li>
        <li>50 eventi/anno</li>
        <li>1 GB di storage</li>
      </ul>
      
      <h4>Funzionalità esclusive Premium (in aggiunta al Free):</h4>
      <ul>
        <li><strong>Comunicazioni Email/WhatsApp</strong> - Invio comunicazioni ai volontari e soci</li>
        <li><strong>Kit Soci completo</strong> - Documenti amministrativi per soci</li>
        <li><strong>Archivio Documenti</strong> - Storage fino a 1GB</li>
        <li><strong>Certificati base</strong> - Attestati per volontari</li>
        <li><strong>Report PDF</strong> - Export registro volontari in PDF</li>
        <li><strong>Export (RUNTS)</strong> - Esportazione dati per RUNTS</li>
        <li><strong>Calendario Scadenzario</strong> - Gestione scadenze con alert</li>
      </ul>
      
      <h4>Non incluso nel Premium (richiede Governance+):</h4>
      <ul>
        <li>Modulo Governance (Organi, Assemblee, Consiglio)</li>
        <li>Rendicontazione economica</li>
        <li>Magazzino e Automezzi (richiede Pro+)</li>
        <li>Statistiche avanzate (richiede Pro+)</li>
      </ul>
    `
  },
  {
    id: 2,
    title: "Archivio Documenti",
    icon: FileText,
    content: `
      <p>Con il Piano Premium hai accesso all'archivio documentale completo con <strong>1GB di storage</strong>.</p>
      
      <h4>Esempi di documenti che puoi caricare:</h4>
      <ul>
        <li>statuto</li>
        <li>regolamento interno</li>
        <li>documenti del presidente</li>
        <li>certificazioni</li>
        <li>consensi privacy dei volontari</li>
      </ul>
      
      <h4>Categorie Documenti Disponibili</h4>
      <p>I documenti possono essere organizzati nelle seguenti categorie:</p>
      <ul>
        <li><strong>Statuto</strong> - Lo statuto dell'associazione</li>
        <li><strong>Regolamento</strong> - Regolamenti interni</li>
        <li><strong>Atto Costitutivo</strong> - Documento di costituzione dell'ente</li>
        <li><strong>Delibere</strong> - Delibere del consiglio direttivo</li>
        <li><strong>Verbali di Assemblea</strong> - Verbali delle assemblee dei soci</li>
        <li><strong>Privacy</strong> - Documenti relativi alla privacy e GDPR</li>
        <li><strong>Liberatoria</strong> - Liberatorie e autorizzazioni</li>
        <li><strong>Modulistica</strong> - Moduli e template</li>
        <li><strong>Assicurativo</strong> - Polizze e documenti assicurativi</li>
        <li><strong>Report</strong> - Report e rendiconti</li>
        <li><strong>Altro</strong> - Altri documenti</li>
      </ul>
      
      <h4>Caricare un documento</h4>
      <ol>
        <li>Aprire la sezione <strong>Documenti</strong></li>
        <li>Cliccare su <strong>Carica documento</strong></li>
        <li>Selezionare il file (max 10MB per file)</li>
        <li>Inserire un nome descrittivo</li>
        <li>Salvare</li>
      </ol>
      
      <h4>Backup Documenti</h4>
      <p>Puoi scaricare tutti i tuoi documenti in un file ZIP dalla sezione Documenti cliccando su <strong>Scarica Backup</strong>.</p>
    `
  },
  {
    id: 3,
    title: "Rendicontazione Sociale",
    icon: Heart,
    content: `
      <p>La Rendicontazione Sociale permette di generare report di impatto sociale per la tua associazione.</p>
      
      <h4>Il report include:</h4>
      <ul>
        <li>numero di volontari attivi</li>
        <li>ore di volontariato donate</li>
        <li>attività realizzate</li>
        <li>beneficiari raggiunti</li>
        <li>valore economico del volontariato</li>
      </ul>
      
      <h4>Generare il Report Sociale</h4>
      <ol>
        <li>Aprire <strong>Report > Rendicontazione Sociale</strong></li>
        <li>Selezionare l'anno di riferimento</li>
        <li>Cliccare su <strong>Genera Report</strong></li>
        <li>Esportare in PDF o stampare</li>
      </ol>
    `
  },
  {
    id: 4,
    title: "Contabilità - Entrate e Uscite",
    icon: Euro,
    content: `
      <p>Il modulo Contabilità permette di gestire la rendicontazione economica dell'ente.</p>
      
      <h4>Registrare Entrate</h4>
      <p>Per registrare un'entrata (donazioni, contributi, quote associative):</p>
      <ol>
        <li>Andare su <strong>Report > Contabilità</strong></li>
        <li>Selezionare la tab <strong>Entrate</strong></li>
        <li>Cliccare su <strong>Nuova Entrata</strong></li>
        <li>Inserire importo, data, categoria e descrizione</li>
        <li>Salvare</li>
      </ol>
      
      <h4>Categorie Entrate disponibili:</h4>
      <ul>
        <li>Quote associative</li>
        <li>Donazioni</li>
        <li>5x1000</li>
        <li>Contributi pubblici</li>
        <li>Contributi privati</li>
        <li>Attività commerciali</li>
        <li>Vendita beni/servizi</li>
        <li>Altro</li>
      </ul>
      
      <h4>Registrare Uscite</h4>
      <p>Per registrare una spesa:</p>
      <ol>
        <li>Selezionare la tab <strong>Uscite</strong></li>
        <li>Cliccare su <strong>Nuova Uscita</strong></li>
        <li>Inserire importo, data, categoria e descrizione</li>
        <li>Salvare</li>
      </ol>
      
      <h4>Categorie Uscite disponibili:</h4>
      <ul>
        <li>Personale</li>
        <li>Affitto locali</li>
        <li>Utenze</li>
        <li>Materiali</li>
        <li>Attività istituzionali</li>
        <li>Formazione</li>
        <li>Assicurazioni</li>
        <li>Spese bancarie</li>
        <li>Consulenze</li>
        <li>Imposte e tasse</li>
        <li>Altro</li>
      </ul>
      
      <h4>Esportare i Dati</h4>
      <p>I dati economici possono essere esportati:</p>
      <ul>
        <li><strong>PDF</strong> - Report formattato per la stampa</li>
        <li><strong>CSV</strong> - Dati grezzi per Excel</li>
      </ul>
    `
  },
  {
    id: 5,
    title: "Certificati Personalizzati",
    icon: Award,
    content: `
      <p>Con il Piano Premium puoi generare certificati personalizzati per i tuoi volontari.</p>
      
      <h4>Template disponibili (Premium):</h4>
      <ul>
        <li><strong>Attestato di Partecipazione</strong> - Certifica la partecipazione alle attività</li>
        <li><strong>Certificato Ore Volontariato</strong> - Attesta le ore svolte in un anno</li>
      </ul>
      
      <h4>Generare un certificato singolo</h4>
      <ol>
        <li>Aprire <strong>Strumenti Avanzati > Certificati</strong></li>
        <li>Selezionare il volontario</li>
        <li>Scegliere il template</li>
        <li>Selezionare l'anno</li>
        <li>Cliccare su <strong>Genera PDF</strong></li>
      </ol>
      
      <h4>Generazione Batch</h4>
      <p>Puoi generare tutti i certificati per i volontari attivi in un unico file ZIP:</p>
      <ol>
        <li>Aprire la tab <strong>Generazione Batch</strong></li>
        <li>Selezionare il template e l'anno</li>
        <li>Scegliere se includere solo volontari con presenze</li>
        <li>Cliccare su <strong>Genera Certificati (ZIP)</strong></li>
      </ol>
      
      <p>Il certificato include automaticamente il logo dell'ente, i dati del volontario e le statistiche delle ore svolte.</p>
    `
  },
  {
    id: 6,
    title: "Comunicazioni a Volontari e Soci",
    icon: Mail,
    content: `
      <p>La sezione Comunicazioni permette di contattare <strong>Volontari</strong> o <strong>Soci</strong> tramite email o WhatsApp.</p>
      
      <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
        <p class="font-semibold text-amber-800">⚠️ IMPORTANTE: Come funziona l'invio email</p>
        <p class="text-amber-700 text-sm">Il modulo Comunicazioni <strong>non invia email automaticamente dal server</strong>. Utilizza il client di posta del tuo computer o le webmail (Gmail, Outlook). Leggi la sezione "Configurazione Invio Email" più sotto per evitare problemi.</p>
      </div>
      
      <h4>Selezionare Destinatari</h4>
      <ol>
        <li>Aprire la sezione <strong>Comunicazioni</strong></li>
        <li>Scegliere il tipo di destinatari cliccando su <strong>Volontari</strong> o <strong>Soci</strong></li>
        <li>Selezionare i singoli destinatari o usare i filtri rapidi:
          <ul>
            <li><strong>Seleziona tutti</strong> - seleziona tutti i destinatari visibili</li>
            <li><strong>Solo con email</strong> - seleziona solo chi ha un indirizzo email</li>
            <li><strong>Solo con telefono</strong> - seleziona solo chi ha un numero di telefono</li>
          </ul>
        </li>
      </ol>
      
      <h4>Inviare Email</h4>
      <p>Dopo aver selezionato i destinatari:</p>
      <ul>
        <li><strong>Apri in Gmail</strong> - Si apre Gmail Web con i destinatari precompilati</li>
        <li><strong>Apri in Outlook</strong> - Si apre Outlook Web con i destinatari precompilati</li>
        <li><strong>Apri la tua casella</strong> - Usa il client di posta predefinito (mailto)</li>
      </ul>
      
      <h4>Inviare Messaggio WhatsApp</h4>
      <ol>
        <li>Selezionare un destinatario con numero di telefono</li>
        <li>Cliccare sul pulsante <strong>Apri</strong> accanto al destinatario</li>
        <li>Si aprirà WhatsApp Web con il contatto preselezionato</li>
      </ol>
      
      <h4>Copiare Indirizzi Email</h4>
      <p>Puoi copiare tutti gli indirizzi email selezionati con il pulsante <strong>Copia indirizzi</strong>. Utile per incollarli manualmente in qualsiasi client di posta.</p>
      
      <h4>⚙️ Configurazione Invio Email</h4>
      <p>Se cliccando sui pulsanti email si apre l'account sbagliato (es. la webmail del tuo provider hosting invece di Gmail), segui queste istruzioni:</p>
      
      <p><strong>Soluzione 1: Usa i pulsanti Gmail/Outlook Web</strong></p>
      <ol>
        <li>Prima di cliccare, assicurati di essere già loggato nell'account Gmail o Outlook corretto nel browser</li>
        <li>Se hai più account, fai prima il logout da quelli che non ti servono</li>
        <li>Clicca su <strong>Apri in Gmail</strong> o <strong>Apri in Outlook</strong></li>
      </ol>
      
      <p><strong>Soluzione 2: Copia e Incolla</strong></p>
      <ol>
        <li>Clicca su <strong>Copia indirizzi email</strong></li>
        <li>Apri manualmente Gmail, Outlook o il tuo client preferito</li>
        <li>Crea un nuovo messaggio e incolla gli indirizzi nel campo "A:"</li>
      </ol>
      
      <p><strong>Soluzione 3: Configura il browser (avanzato)</strong></p>
      <ul>
        <li><strong>Chrome:</strong> Impostazioni → Privacy e sicurezza → Impostazioni sito → Gestori protocollo → Imposta Gmail come gestore mailto</li>
        <li><strong>Firefox:</strong> Impostazioni → Applicazioni → Cerca "mailto" → Scegli "Usa Gmail"</li>
        <li><strong>Edge:</strong> Impostazioni → Cookie e autorizzazioni sito → Gestori protocolli</li>
      </ul>
      
      <p class="text-sm text-slate-500 mt-4"><em>Suggerimento: Se hai dubbi, chiedi aiuto al Chatbot AI in basso a destra!</em></p>
    `
  },
  {
    id: 7,
    title: "Calendario Scadenzario",
    icon: Clock,
    content: `
      <p>Il <strong>Calendario Scadenzario</strong> è uno strumento unificato per gestire tutte le scadenze dell'ente in un unico posto.</p>
      
      <h4>Tipi di scadenze gestibili:</h4>
      <ul>
        <li><strong style="color:#3B82F6">Eventi</strong> - Riunioni, assemblee, attività programmate</li>
        <li><strong style="color:#22C55E">Pagamenti/Quote</strong> - Quote sociali, rate, fatture</li>
        <li><strong style="color:#F97316">Assicurazioni</strong> - Polizze RC, infortuni, CAT NAT</li>
        <li><strong style="color:#A855F7">Documenti</strong> - Comunicazioni istituzionali, scadenze legali</li>
        <li><strong style="color:#EF4444">Utenze/Fornitori</strong> - Bollette, contratti, abbonamenti</li>
      </ul>
      
      <h4>Come accedere</h4>
      <ol>
        <li>Dal menu laterale, vai su <strong>ATTIVITÀ → Calendario Scadenze</strong></li>
        <li>Visualizza il calendario mensile o la lista delle scadenze</li>
      </ol>
      
      <h4>Creare una nuova scadenza</h4>
      <ol>
        <li>Clicca su <strong>Nuova Scadenza</strong> (o clicca su un giorno nel calendario)</li>
        <li>Inserisci:
          <ul>
            <li>Titolo (obbligatorio)</li>
            <li>Data scadenza</li>
            <li>Tipo (evento, pagamento, assicurazione, ecc.)</li>
            <li>Importo (opzionale)</li>
            <li>Ricorrenza (una tantum, mensile, annuale)</li>
          </ul>
        </li>
        <li>Clicca <strong>Crea Scadenza</strong></li>
      </ol>
      
      <h4>Scadenze automatiche</h4>
      <p>Il calendario mostra automaticamente:</p>
      <ul>
        <li>Gli <strong>eventi</strong> già creati nella sezione Eventi</li>
        <li>Le <strong>scadenze polizze</strong> inserite nelle Coperture</li>
      </ul>
      <p>Queste scadenze "auto" sono contrassegnate con un badge e non possono essere modificate direttamente.</p>
      
      <h4>Prossime scadenze</h4>
      <p>Nella colonna laterale vedi sempre le <strong>scadenze dei prossimi 30 giorni</strong>, con un avviso colorato per quelle più urgenti (entro 7 giorni).</p>
      
      <h4>Segnare come completata</h4>
      <p>Puoi segnare una scadenza come completata cliccando sull'icona ✓. Le scadenze completate appaiono barrate e con opacità ridotta.</p>
    `
  },
  {
    id: 8,
    title: "FAQ Piano Premium",
    icon: HelpCircle,
    content: `
      <h4>Quanto costa il Piano Premium?</h4>
      <p>Il Piano Premium costa €9,99/mese (IVA inclusa).</p>
      
      <h4>Quanti volontari posso gestire?</h4>
      <p>Con il piano Premium puoi gestire fino a <strong>60 volontari attivi</strong> e <strong>100 soggetti unici</strong>.</p>
      
      <h4>Quanto spazio ho per i documenti?</h4>
      <p>Il piano Premium include <strong>1 GB</strong> di storage per i documenti.</p>
      
      <h4>Cosa include il piano Premium?</h4>
      <p>Tutto il piano Free + Comunicazioni Email/WhatsApp, Kit Soci completo, Archivio documenti, Certificati base, Report PDF, Export (RUNTS), Calendario Scadenzario.</p>
      
      <h4>Posso usare la Contabilità Economica?</h4>
      <p><strong>No</strong>. La Rendicontazione Economica richiede il piano <strong>Governance</strong> (€14,99/mese) o superiore.</p>
      
      <h4>Posso usare il Magazzino o gli Automezzi?</h4>
      <p><strong>No</strong>. Magazzino e Automezzi richiedono il piano <strong>Pro</strong> (€24,99/mese) o superiore.</p>
      
      <h4>Qual è la differenza con il piano Governance?</h4>
      <p>Il piano Governance (€14,99/mese) aggiunge: Modulo Governance completo (Organi Sociali, Assemblee, Consiglio Direttivo), Rendicontazione Economica (35 categorie), Certificati personalizzati, Libri Sociali PDF, Import Massivo (max 200), Schemi Ministeriali (MOD. D, C).</p>
    `
  },
  {
    id: 9,
    title: "Gestione Soci e Associati",
    icon: Users,
    content: `
      <p>Il modulo <strong>Soci e Associati</strong> permette di gestire l'anagrafica dei soci dell'associazione separatamente dai volontari.</p>
      
      <h4>Differenza tra Soci e Volontari</h4>
      <ul>
        <li><strong>Socio</strong> - persona iscritta all'associazione che paga la quota associativa</li>
        <li><strong>Volontario</strong> - persona che presta attività di volontariato</li>
        <li>Una stessa persona può essere sia socio che volontario</li>
      </ul>
      
      <h4>Inserire un nuovo Socio</h4>
      <ol>
        <li>Aprire la sezione <strong>Soci e Associati - Tutti i Soci</strong></li>
        <li>Cliccare su <strong>Nuovo Socio</strong></li>
        <li>Inserire i dati anagrafici (nome, cognome, codice fiscale, contatti)</li>
        <li>Selezionare il tipo di socio (ordinario, sostenitore, onorario, fondatore)</li>
        <li>Il numero tessera viene generato automaticamente</li>
        <li>Salvare</li>
      </ol>
      
      <h4>Tipi di Socio</h4>
      <ul>
        <li><strong>Ordinario</strong> - socio standard con diritto di voto</li>
        <li><strong>Sostenitore</strong> - socio che contribuisce economicamente</li>
        <li><strong>Onorario</strong> - socio nominato per meriti speciali</li>
        <li><strong>Fondatore</strong> - socio presente dalla costituzione dell'ente</li>
      </ul>
      
      <h4>Sospendere, Cessare o Gestire lo Stato di un Socio</h4>
      <p>Dal menu azioni di ogni socio puoi cambiare lo stato:</p>
      <ul>
        <li><strong>Sospendere</strong> - il socio rimane in archivio ma non è più attivo temporaneamente</li>
        <li><strong>Riattivare</strong> - ripristina lo stato attivo del socio sospeso</li>
        <li><strong>Cessare</strong> - il socio termina la sua appartenenza all'associazione</li>
        <li><strong>Cessare per Morosità</strong> - cessazione dovuta al mancato pagamento delle quote</li>
        <li><strong>Deceduto</strong> - registra il decesso del socio mantenendo lo storico</li>
      </ul>
      <p><em>Nota: I soci cessati o deceduti rimangono nell'archivio storico ma non compaiono più tra i soci attivi.</em></p>
      
      <h4>Registro Soci</h4>
      <p>La sezione <strong>Registro Soci</strong> mostra il libro dei soci ufficiale con:</p>
      <ul>
        <li>Elenco completo con numero tessera progressivo</li>
        <li>Filtro per anno di iscrizione</li>
        <li>Export in formato CSV per stampa</li>
      </ul>
      
      <h4>Kit Amministrativo Soci</h4>
      <p>Nella sezione <strong>SOCI E ASSOCIATI → Kit Amministrativo Soci</strong> trovi modelli pronti per:</p>
      <ul>
        <li>Domanda di ammissione a socio</li>
        <li>Convocazione assemblea</li>
        <li>Verbale assemblea</li>
        <li>Ricevuta quota associativa</li>
        <li>Delega partecipazione assemblea</li>
      </ul>
      <p><strong>Nota sul numero tessera:</strong> Nei documenti generati (es. ricevuta quota) viene utilizzato il <strong>numero tessera inserito manualmente</strong> dall'utente (campo "Tessera Associazione" nell'anagrafica socio). Se non compilato, viene mostrato il numero progressivo auto-generato dal sistema.</p>
      
      <h4>Export</h4>
      <p>La sezione <strong>REPORT E CONTABILITÀ → Export</strong> permette di esportare i dati nel formato richiesto dal Registro Unico Nazionale del Terzo Settore:</p>
      <ul>
        <li><strong>Elenco Soci</strong> - CSV con dati anagrafici, date iscrizione/cessazione, qualifica</li>
        <li><strong>Registro Volontari</strong> - Conforme D.Lgs. 117/2017 con numerazione progressiva</li>
        <li><strong>Organi Sociali</strong> - Cariche con nomenclatura RUNTS (Organo di Amministrazione, ecc.)</li>
        <li><strong>Riepilogo Numerico</strong> - Conteggi per comunicazione annuale (entro 30/06)</li>
      </ul>
      <p><em>Ricorda: Entro il 30 giugno di ogni anno devi comunicare al RUNTS il numero di soci, volontari e lavoratori riferiti al 31/12 dell'anno precedente.</em></p>
      <p><em>Nota: Il Kit Soci è diverso dal Kit Amministrativo ETS (per volontari), che si trova sotto VOLONTARI.</em></p>
      
      <h4>Schemi Ministeriali Bilancio (Piano Governance+ / Pro+)</h4>
      <p>La sezione <strong>REPORT E CONTABILITÀ → Schemi Ministeriali</strong> permette di generare i bilanci ufficiali secondo il D.M. 5 marzo 2020 n. 39:</p>
      <ul>
        <li><strong>MOD. D - Rendiconto per Cassa</strong> (Governance+) - Per ETS con ricavi inferiori a €220.000/anno. Schema semplificato con entrate e uscite suddivise per sezione ministeriale</li>
        <li><strong>MOD. C - Relazione di Missione</strong> (Governance+) - Documento narrativo obbligatorio per tutti gli ETS. Template precompilato con dati statistici dell'ente</li>
        <li><strong>MOD. A - Stato Patrimoniale</strong> (Pro+) - Per ETS con ricavi superiori a €220.000. Attivo e Passivo patrimoniale (versione semplificata da cassa)</li>
        <li><strong>MOD. B - Rendiconto Gestionale</strong> (Pro+) - Per ETS con ricavi superiori a €220.000. Oneri e Proventi (versione semplificata da cassa)</li>
      </ul>
      <p><strong>Come funziona:</strong></p>
      <ol>
        <li>Le transazioni inserite nella <strong>Contabilità Economica</strong> vengono automaticamente classificate secondo le voci ministeriali</li>
        <li>Seleziona l'anno di riferimento</li>
        <li>Verifica l'anteprima dei dati</li>
        <li>Scarica il PDF pronto per il deposito al RUNTS o per il commercialista</li>
      </ol>
      <p><em>Nota: Il sistema verifica automaticamente se il MOD. D è applicabile (ricavi &lt; €220.000) o se è necessario il MOD. A/B. MOD. A e B sono versioni semplificate derivate dai dati di cassa - per un bilancio completo consultare un commercialista.</em></p>
    `
  },
  {
    id: 10,
    title: "Quote Associative",
    icon: Euro,
    content: `
      <p>Le <strong>Quote Associative</strong> permettono di gestire i pagamenti annuali dei soci.</p>
      
      <h4>Creare una Quota</h4>
      <ol>
        <li>Aprire <strong>Soci e Associati - Quote Associative</strong></li>
        <li>Cliccare su <strong>Nuova Quota</strong></li>
        <li>Selezionare il socio</li>
        <li>Inserire anno e importo</li>
        <li>La quota viene creata con stato "Da pagare"</li>
      </ol>
      
      <h4>Registrare un Pagamento</h4>
      <ol>
        <li>Trovare la quota nella lista</li>
        <li>Cliccare su <strong>Paga</strong></li>
        <li>Selezionare il metodo di pagamento (contanti, bonifico, carta)</li>
        <li>Inserire la <strong>data effettiva di pagamento</strong></li>
        <li>Inserire eventuale numero ricevuta</li>
        <li>Confermare</li>
      </ol>
      
      <h4>Gestione Acconti e Integrazioni</h4>
      <p>Se un socio ha già una quota per l'anno e vuoi registrare un <strong>acconto o integrazione</strong>:</p>
      <ol>
        <li>Crea una nuova quota per lo stesso socio e anno</li>
        <li>Il sistema ti avviserà che esiste già una quota</li>
        <li>Seleziona il tipo: <strong>Integrazione</strong>, <strong>Saldo</strong> o <strong>Acconto</strong></li>
        <li>Clicca "Crea Integrazione" per procedere</li>
      </ol>
      <p><em>Esempio: Quota annuale €50, il socio paga €30 subito (acconto) e €20 dopo (saldo).</em></p>
      
      <h4>Statistiche Quote</h4>
      <p>Nella parte superiore della pagina vedi:</p>
      <ul>
        <li>Totale quote emesse</li>
        <li>Quote pagate vs da pagare</li>
        <li>Importo totale incassato</li>
      </ul>
      
      <h4>Eliminazione Quota</h4>
      <p>Per eliminare una quota inserita erroneamente:</p>
      <ol>
        <li>Trovare la quota nella lista</li>
        <li>Cliccare sull'icona <strong>cestino rosso</strong> nella colonna Azioni</li>
        <li>Confermare l'eliminazione nel popup</li>
      </ol>
      <p><em>Nota: l'eliminazione e definitiva e non puo essere annullata.</em></p>
      
      <h4>Contabilita Automatica</h4>
      <p>Quando registri un pagamento, il sistema crea automaticamente un'<strong>entrata in contabilita</strong> nella sezione Rendicontazione Economica, con:</p>
      <ul>
        <li>Categoria: "Quote associative"</li>
        <li>Descrizione: nome del socio e anno</li>
        <li>Modalita incasso mappata dal metodo di pagamento</li>
      </ul>
      
      <h4>Accesso Rapido dalla Dashboard</h4>
      <p>Dalla Dashboard puoi accedere velocemente alle quote con i pulsanti:</p>
      <ul>
        <li><strong>Quote da Incassare:</strong> vedi tutti i soci con pagamento in sospeso</li>
        <li><strong>Quote Incassate:</strong> vedi l'elenco dei pagamenti ricevuti</li>
      </ul>
      
      <h4>Filtri</h4>
      <p>Puoi filtrare le quote per:</p>
      <ul>
        <li>Anno (2026, 2025, ecc.)</li>
        <li>Stato (da pagare, pagate, scadute)</li>
      </ul>
    `
  },
  {
    id: 11,
    title: "Diario Attività",
    icon: BookOpen,
    content: `
      <p>Il <strong>Diario Attività</strong> è un registro cronologico di tutte le attività svolte dall'ente.</p>
      
      <h4>Cosa registra il diario:</h4>
      <ul>
        <li>Eventi e attività completate</li>
        <li>Ore di volontariato per giornata</li>
        <li>Partecipanti per ogni attività</li>
        <li>Note e osservazioni</li>
      </ul>
      
      <h4>Consultare il diario</h4>
      <ol>
        <li>Aprire la sezione <strong>Diario Attività</strong></li>
        <li>Selezionare il periodo desiderato</li>
        <li>Filtrare per tipo di attività o volontario</li>
        <li>Visualizzare il riepilogo</li>
      </ol>
      
      <h4>Utilità del diario</h4>
      <p>Il diario è utile per:</p>
      <ul>
        <li>Rendicontazione sociale</li>
        <li>Report per enti finanziatori</li>
        <li>Storico delle attività dell'ente</li>
        <li>Verifica presenze e partecipazione</li>
      </ul>
    `
  },
    {
    title: "Gestione Magazzino (Piano PRO)",
    icon: Package,
    content: `
      <p>Il modulo <strong>Magazzino</strong> consente di gestire l'inventario di materiali, dotazioni e attrezzature dell'associazione.</p>
      
      <h4>Inventario Articoli</h4>
      <p>Dal menu <strong>MAGAZZINO → Inventario</strong>:</p>
      <ul>
        <li>Aggiungi articoli con nome, categoria, numero seriale, quantita e taglia</li>
        <li>Categorie disponibili: Radio, Divise, Antenne, PC Portatili, Accessori, Attrezzatura Tecnica, ecc.</li>
        <li>Per ogni articolo il sistema calcola automaticamente: quantita totale, in uso e disponibile</li>
        <li>Filtri per categoria e ricerca testuale</li>
        <li>Export PDF dell'inventario completo su carta intestata</li>
      </ul>

      <h4>Assegnazioni Materiale</h4>
      <p>Dal menu <strong>MAGAZZINO → Assegnazioni</strong>:</p>
      <ol>
        <li>Clicca "Nuova Assegnazione"</li>
        <li>Seleziona l'articolo (mostra solo quelli disponibili)</li>
        <li>Seleziona il volontario destinatario</li>
        <li>Indica quantita e taglia (per le divise)</li>
        <li>Conferma la consegna</li>
      </ol>
      <p>Per registrare una <strong>restituzione</strong>: clicca "Reso" accanto all'assegnazione attiva, indica la condizione dell'oggetto (buono, danneggiato, da sostituire, fuori uso).</p>
    `
    },
    {
    title: "Gestione Automezzi (Piano PRO)",
    icon: Car,
    content: `
      <p>Il modulo <strong>Automezzi</strong> consente di gestire il parco veicoli dell'associazione con assicurazioni, revisioni e manutenzioni.</p>
      
      <h4>Registrazione Veicolo</h4>
      <p>Dal menu <strong>MAGAZZINO → Automezzi</strong>:</p>
      <ol>
        <li>Clicca "Nuovo Automezzo"</li>
        <li>Inserisci: Targa, Marca, Modello, Tipo veicolo, Alimentazione, Anno, Km attuali</li>
        <li>Salva</li>
      </ol>

      <h4>Gestione Assicurazioni</h4>
      <p>Clicca <strong>"Gestisci"</strong> su un veicolo → tab <strong>Assicurazioni</strong>:</p>
      <ul>
        <li>Registra polizze con: compagnia, numero polizza, date, tipo copertura</li>
        <li>Frazionamento: annuale, semestrale, quadrimestrale, trimestrale, temporanea, premio unico</li>
        <li>Premio annuo e premio rata (per polizze rateizzate)</li>
        <li>Le scadenze vengono create automaticamente nello Scadenzario Generale</li>
        <li>Per polizze rateizzate: vengono create anche le scadenze intermedie delle rate</li>
      </ul>

      <h4>Gestione Revisioni</h4>
      <p>Tab <strong>Revisioni</strong>: registra data, esito (superata/non superata), km, officina e prossima scadenza. La prossima scadenza viene aggiunta allo Scadenzario.</p>

      <h4>Gestione Manutenzioni</h4>
      <p>Tab <strong>Manutenzioni</strong>: registra interventi con tipo (ordinaria, straordinaria, tagliando, gomme), descrizione, km, officina e costo. I km vengono aggiornati automaticamente sulla scheda veicolo.</p>

      <h4>Alert Scadenze</h4>
      <p>Nella lista automezzi, le colonne Assicurazione e Revisione mostrano badge colorati:</p>
      <ul>
        <li><strong>Verde</strong>: in regola (data scadenza)</li>
        <li><strong>Ambra</strong>: in scadenza (entro 30 giorni)</li>
        <li><strong>Rosso</strong>: scaduto</li>
      </ul>
    `
    },
    {
    title: "Assistente AI (Chatbot)",
    icon: HelpCircle,
    content: `
      <p>Assietica Gest include un <strong>Assistente AI</strong> che può rispondere alle tue domande sulla piattaforma.</p>
      
      <h4>Come usare l'assistente</h4>
      <ol>
        <li>Clicca sull'icona del chatbot in basso a destra</li>
        <li>Scrivi la tua domanda in italiano</li>
        <li>L'assistente ti risponderà in tempo reale</li>
      </ol>
      
      <h4>Cosa puoi chiedere:</h4>
      <ul>
        <li>Come usare una funzionalità specifica</li>
        <li>Dove trovare una sezione</li>
        <li>Come risolvere un problema</li>
        <li>Spiegazioni su report e statistiche</li>
        <li>Informazioni sui piani e prezzi</li>
      </ul>
      
      <h4>Suggerimenti</h4>
      <p>Per ottenere risposte migliori:</p>
      <ul>
        <li>Fai domande specifiche e chiare</li>
        <li>Descrivi il problema che stai riscontrando</li>
        <li>Indica la sezione in cui ti trovi</li>
      </ul>
    `
  },
  {
    id: 13,
    title: "Convenzioni e Sconti",
    icon: Heart,
    content: `
      <p>Se la tua associazione fa parte di una <strong>federazione nazionale</strong> (es. ANPAS, ANFFAS, Croce Rossa, ecc.), potresti avere diritto a <strong>condizioni economiche agevolate</strong>.</p>
      
      <h4>Come funziona</h4>
      <p>La federazione nazionale stipula una convenzione con Assietica Gest e riceve un <strong>codice convenzione</strong> da distribuire agli enti affiliati.</p>
      
      <h4>Aderire a una Convenzione</h4>
      <p>Puoi inserire il codice convenzione in due momenti:</p>
      
      <p><strong>1. Durante la registrazione (Onboarding):</strong></p>
      <ol>
        <li>Nel form "Dati Ente" trovi il campo "Hai un codice convenzione?"</li>
        <li>Inserisci il codice ricevuto dalla federazione</li>
        <li>Il sistema verifica automaticamente e ti mostra se è valido</li>
        <li>Lo sconto viene applicato al completamento dell'onboarding</li>
      </ol>
      
      <p><strong>2. Dopo la registrazione:</strong></p>
      <ol>
        <li>Vai in <strong>Impostazioni → sezione Convenzione</strong></li>
        <li>Inserisci il <strong>codice convenzione</strong> ricevuto dalla federazione (es. ANPAS-2026-XK9F)</li>
        <li>Clicca su <strong>Aderisci</strong></li>
        <li>Lo sconto viene applicato automaticamente!</li>
      </ol>
      
      <h4>Vantaggi</h4>
      <ul>
        <li><strong>Sconto percentuale</strong> sul canone (es. 20% di sconto)</li>
        <li>oppure <strong>Prezzo fisso convenzionato</strong></li>
        <li>Valido per tutta la durata della convenzione</li>
      </ul>
      
      <h4>Verificare la Convenzione Attiva</h4>
      <p>Dopo l'adesione, nella sezione Impostazioni → Convenzione vedrai:</p>
      <ul>
        <li>Nome della convenzione attiva</li>
        <li>Sconto applicato</li>
        <li>Data di adesione</li>
        <li>Eventuale scadenza</li>
      </ul>
      
      <p><em>Non hai un codice? Contatta la tua federazione nazionale o l'assistenza Assietica Gest.</em></p>
    `
  }
];

// ============= PIANO GOVERNANCE =============
const chaptersGovernance = [
  {
    id: 1,
    title: "Introduzione al Piano Governance",
    icon: Landmark,
    content: `
      <div class="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
        <p class="font-semibold text-indigo-800">⚠️ IMPORTANTE: Piano Governance richiesto</p>
        <p class="text-indigo-700 text-sm">Le funzionalità di questa sezione (Organi Sociali, Assemblee, Consiglio Direttivo) sono disponibili <strong>esclusivamente</strong> dal piano <strong>Governance (€14,99/mese IVA inclusa)</strong> in su.</p>
        <p class="text-indigo-700 text-sm mt-1">I piani <strong>Free</strong> e <strong>Premium</strong> non includono il Modulo Governance.</p>
      </div>
      
      <p>Il <strong>Piano Governance</strong> (€14,99/mese IVA inclusa) è pensato per associazioni strutturate con esigenze di governance avanzata e rendicontazione economica.</p>
      
      <h4>Limiti Piano Governance:</h4>
      <ul>
        <li><strong>100 volontari attivi</strong></li>
        <li><strong>200 soggetti unici</strong></li>
        <li>100 eventi/anno</li>
        <li>2 GB di storage</li>
        <li>1 utente per ente</li>
      </ul>
      
      <h4>Funzionalità esclusive Governance (non presenti in Free/Premium):</h4>
      <ul>
        <li><strong>Modulo Governance:</strong>
          <ul>
            <li>Organi Sociali - Gestione cariche, mandati, scadenze</li>
            <li>Assemblee - Convocazioni, verbali, presenze, deleghe</li>
            <li>Consiglio Direttivo - Gestione riunioni e delibere</li>
          </ul>
        </li>
        <li><strong>Libri Sociali (PDF):</strong>
          <ul>
            <li>Libro dei Consigli Direttivi</li>
            <li>Libro delle Assemblee</li>
            <li>Libro degli Eventi</li>
            <li>Libro Attività Completo (unificato)</li>
          </ul>
        </li>
        <li><strong>Rendicontazione Economica</strong> - 35 categorie semplificate per entrate/uscite</li>
        <li><strong>Certificati personalizzati</strong> - Generazione batch (max 20)</li>
        <li><strong>Comunicazioni di gruppo</strong> - Email a gruppi di volontari</li>
        <li><strong>Log operazioni completo</strong> - Audit trail</li>
      </ul>
      
      <p>Tutte le funzionalità dei piani Free e Premium sono incluse.</p>
    `
  },
  {
    id: 2,
    title: "Rendicontazione Economica",
    icon: Euro,
    content: `
      <p>La <strong>Rendicontazione Economica</strong> permette di gestire entrate e uscite dell'ente con categorie predefinite conformi al D.Lgs. 117/2017.</p>
      
      <h4>Piano Governance: 35 Categorie Semplificate</h4>
      <p>Il piano Governance include un set semplificato di categorie adatto alla maggior parte delle associazioni.</p>
      
      <h4>Registrare Entrate</h4>
      <ol>
        <li>Andare su <strong>Report > Contabilità Economica</strong></li>
        <li>Selezionare la tab <strong>Entrate</strong></li>
        <li>Cliccare su <strong>Nuova Entrata</strong></li>
        <li>Inserire importo, data, categoria e descrizione</li>
        <li>Salvare</li>
      </ol>
      
      <h4>Registrare Uscite</h4>
      <ol>
        <li>Selezionare la tab <strong>Uscite</strong></li>
        <li>Cliccare su <strong>Nuova Uscita</strong></li>
        <li>Inserire importo, data, categoria e descrizione</li>
        <li>Salvare</li>
      </ol>
      
      <h4>Report e Export</h4>
      <p>Puoi generare report mensili/annuali e esportarli in PDF o CSV per il commercialista.</p>
      
      <p><em>Nota: Per accedere a tutte le 120+ categorie e alla gestione progetti, è necessario il piano Pro (€24,99/mese IVA inclusa).</em></p>
    `
  },
  {
    id: 3,
    title: "Organi Sociali e Governance",
    icon: Landmark,
    content: `
      <p>La sezione <strong>Organi Sociali</strong> permette di gestire la struttura di governance dell'associazione.</p>
      
      <h4>Gestione Cariche</h4>
      <p>Puoi registrare tutte le cariche sociali:</p>
      <ul>
        <li>Presidente</li>
        <li>Vice-Presidente</li>
        <li>Segretario</li>
        <li>Tesoriere</li>
        <li>Consiglieri</li>
        <li>Revisori dei conti</li>
      </ul>
      
      <h4>Scadenze Mandati</h4>
      <p>Il sistema tiene traccia delle scadenze dei mandati e invia notifiche automatiche:</p>
      <ul>
        <li>30 giorni prima della scadenza</li>
        <li>15 giorni prima</li>
        <li>7 giorni prima</li>
      </ul>
      
      <h4>Storico Cariche</h4>
      <p>Puoi consultare lo storico completo delle persone che hanno ricoperto ciascuna carica nel tempo.</p>
    `
  },
  {
    id: 4,
    title: "Assemblee",
    icon: Users,
    content: `
      <p>La sezione <strong>Assemblee</strong> permette di gestire le assemblee dei soci in modo completo.</p>
      
      <h4>Creare una Nuova Assemblea</h4>
      <ol>
        <li>Aprire <strong>Governance > Assemblee</strong></li>
        <li>Cliccare su <strong>Nuova Assemblea</strong></li>
        <li>Inserire data, ora, luogo e ordine del giorno</li>
        <li>Salvare</li>
      </ol>
      
      <h4>Convocazione</h4>
      <p>Puoi generare la convocazione in PDF e inviarla via email a tutti i soci.</p>
      
      <h4>Foglio Presenze</h4>
      <p>Registra le presenze e le deleghe dei soci partecipanti.</p>
      
      <h4>Verbale</h4>
      <p>Al termine dell'assemblea, puoi redigere e generare il verbale in PDF.</p>
      
      <h4>Documenti Automatici:</h4>
      <ul>
        <li>Convocazione assemblea</li>
        <li>Foglio presenze</li>
        <li>Modulo delega</li>
        <li>Verbale assemblea</li>
      </ul>
    `
  },
  {
    id: 5,
    title: "Certificati Batch",
    icon: Award,
    content: `
      <p>Con il Piano Governance puoi generare certificati in batch per più volontari.</p>
      
      <h4>Limite Governance: 20 certificati per batch</h4>
      <p>Puoi generare fino a 20 certificati alla volta. Per batch illimitati, passa al piano Pro.</p>
      
      <h4>Come generare certificati batch</h4>
      <ol>
        <li>Aprire <strong>Strumenti Avanzati > Certificati</strong></li>
        <li>Selezionare la tab <strong>Generazione Batch</strong></li>
        <li>Scegliere il template (Partecipazione, Ore Volontariato)</li>
        <li>Selezionare l'anno</li>
        <li>Cliccare su <strong>Genera Certificati (ZIP)</strong></li>
      </ol>
      
      <p>I certificati vengono generati in un file ZIP scaricabile.</p>
    `
  },
  {
    id: 6,
    title: "Import Massivo Soci e Volontari",
    icon: FileUp,
    content: `
      <p>L'<strong>Import Massivo</strong> permette di importare soci e volontari da file esterni (CSV, Excel, ODS).</p>
      
      <div class="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-3">
        <p class="font-semibold text-indigo-800">Limite Piano Governance: max 200 soggetti per import</p>
      </div>
      
      <h4>Formati supportati</h4>
      <ul>
        <li><strong>CSV</strong> - separatore ; o , (auto-rilevato)</li>
        <li><strong>Excel</strong> - .xlsx, .xls</li>
        <li><strong>OpenDocument</strong> - .ods</li>
        <li><strong>Formato Federazioni</strong> - riconoscimento automatico file con 50+ colonne</li>
      </ul>
      
      <h4>Come importare</h4>
      <ol>
        <li>Andare su <strong>Strumenti Avanzati > Import Massivo</strong></li>
        <li><strong>Step 1:</strong> Caricare il file (trascina o clicca)</li>
        <li><strong>Step 2:</strong> Verificare la mappatura campi (automatica o manuale)</li>
        <li><strong>Step 3:</strong> Controllare l'anteprima (validi, duplicati, errori)</li>
        <li><strong>Step 4:</strong> Confermare l'import</li>
      </ol>
      
      <h4>Mappatura automatica</h4>
      <p>Il sistema riconosce automaticamente le colonne piu comuni:</p>
      <ul>
        <li>Cognome, Nome, Codice Fiscale</li>
        <li>Email, Telefono, Cellulare</li>
        <li>Data di nascita, Luogo di nascita</li>
        <li>Indirizzo, CAP, Citta, Provincia</li>
        <li>Numero Tessera, Data Iscrizione, Stato</li>
      </ul>
      
      <h4>Gestione duplicati</h4>
      <p>Se un codice fiscale esiste gia nel sistema, puoi scegliere:</p>
      <ul>
        <li><strong>Salta</strong> - non importa il record</li>
        <li><strong>Aggiorna</strong> - sovrascrive i dati esistenti</li>
      </ul>
      
      <h4>Template vuoti</h4>
      <p>Puoi scaricare template CSV precompilati dalla pagina di import.</p>
      
      <h4>Rollback Import</h4>
      <p>Se hai importato dati errati, puoi annullare un import:</p>
      <ol>
        <li>Nella pagina Import Massivo, clicca su <strong>Rollback Ultimo Import</strong></li>
        <li>Seleziona la data dell'import da annullare (cronologia ultimi 30 giorni)</li>
        <li>Conferma l'eliminazione</li>
      </ol>
      <p><em>Il rollback elimina tutti i record importati in quella data: persone, ruoli (socio/volontario) e quote associative collegate.</em></p>
    `
  },
  {
    id: 7,
    title: "FAQ Piano Governance",
    icon: HelpCircle,
    content: `
      <h4>Quanto costa il Piano Governance?</h4>
      <p>Il Piano Governance costa €14,99/mese (IVA inclusa).</p>
      
      <h4>Quanti volontari posso gestire?</h4>
      <p>Con il piano Governance puoi gestire fino a <strong>100 volontari attivi</strong> e <strong>200 soggetti unici</strong>.</p>
      
      <h4>Cosa include il piano Governance?</h4>
      <p>Tutto del piano Premium + Modulo Governance completo (Organi Sociali, Assemblee, Consiglio Direttivo), Rendicontazione Economica (35 categorie), Certificati personalizzati, Comunicazioni avanzate e template, Log operazioni completo, Libri Sociali PDF, Import Massivo (max 200 soggetti), Schemi Ministeriali Bilancio (MOD. D, C).</p>
      
      <h4>Quante categorie economiche ho?</h4>
      <p>Il piano Governance include <strong>35 categorie semplificate</strong>. Per tutte le 120+ categorie, passa al piano Pro.</p>
      
      <h4>Posso gestire le assemblee?</h4>
      <p>Si, la gestione completa di assemblee, verbali e deleghe è inclusa nel piano Governance.</p>
      
      <h4>Posso usare il Magazzino o gli Automezzi?</h4>
      <p><strong>No</strong>. Gestione Magazzino e Automezzi richiedono il piano <strong>Pro</strong> (€24,99/mese) o superiore.</p>
      
      <h4>Quanti certificati posso generare in batch?</h4>
      <p>Puoi generare fino a <strong>20 certificati per batch</strong>. Per batch illimitati, passa al piano Pro.</p>
      
      <h4>Qual è la differenza con il piano Pro?</h4>
      <p>Il piano Pro (€24,99/mese) aggiunge: Rendicontazione completa (120+ categorie), Statistiche avanzate con grafici, Gestione Magazzino e Assegnazioni, Gestione Automezzi (assicurazioni, revisioni, manutenzioni), Import massivo (max 500), Chatbot AI, batch illimitati, Schemi Ministeriali completi (MOD. A, B).</p>
    `
  }
];

// ============= PIANO PRO =============
const chaptersPro = [
  {
    id: 1,
    title: "Introduzione al Piano Pro",
    icon: Star,
    content: `
      <p>Il <strong>Piano Pro</strong> (€24,99/mese IVA inclusa) è la soluzione completa per enti con esigenze avanzate.</p>
      
      <h4>Limiti Piano Pro:</h4>
      <ul>
        <li><strong>250 volontari attivi</strong></li>
        <li><strong>500 soggetti unici</strong></li>
        <li>Eventi illimitati</li>
        <li>5 GB di storage</li>
      </ul>
      
      <h4>Funzionalità esclusive Pro (in aggiunta al Governance):</h4>
      <ul>
        <li><strong>Rendicontazione completa</strong> - 120+ categorie economiche</li>
        <li><strong>Statistiche Avanzate</strong> - Dashboard con trend, confronti YoY, classifiche</li>
        <li><strong>Gestione Magazzino</strong> - Inventario articoli e assegnazioni a volontari</li>
        <li><strong>Gestione Automezzi</strong> - Assicurazioni, revisioni, manutenzioni con scadenzario automatico</li>
        <li><strong>Import Massivo</strong> - Importa soci/volontari da CSV/Excel (max 500)</li>
        <li><strong>Assistente AI</strong> - Chatbot integrato per supporto</li>
        <li><strong>Certificati Pro</strong> - Template aggiuntivi (Merito, Formazione) + batch illimitati</li>
        <li><strong>Schemi Ministeriali completi</strong> - MOD. A e B (per ETS con entrate &gt; €220k)</li>
        <li><strong>Supporto prioritario</strong></li>
      </ul>
      
      <p>Tutte le funzionalità dei piani Free, Premium e Governance sono incluse.</p>
    `
  },
  {
    id: 2,
    title: "Statistiche Avanzate",
    icon: TrendingUp,
    content: `
      <p>Le Statistiche Avanzate offrono una dashboard completa con analisi dettagliate.</p>
      
      <h4>KPI con Confronti Anno su Anno</h4>
      <p>La dashboard mostra:</p>
      <ul>
        <li>Volontari attivi (con variazione % vs anno precedente)</li>
        <li>Ore di volontariato totali (con trend YoY)</li>
        <li>Eventi realizzati (con confronto annuale)</li>
        <li>Media ore per volontario</li>
      </ul>
      
      <h4>Andamento Mensile</h4>
      <p>Grafici che mostrano:</p>
      <ul>
        <li>Ore di volontariato per mese</li>
        <li>Volontari attivi per mese</li>
        <li>Eventi per mese</li>
      </ul>
      
      <h4>Trend Multi-Anno</h4>
      <p>Visualizza l'andamento delle ore di volontariato negli ultimi 3 anni.</p>
      
      <h4>Distribuzione per Età</h4>
      <p>Grafico a torta che mostra la composizione anagrafica dei volontari per fascia d'età.</p>
      
      <h4>Top 10 Volontari</h4>
      <p>Classifica dei volontari con più ore nell'anno selezionato.</p>
      
      <h4>Performance Eventi</h4>
      <p>Tabella con metriche per ogni evento: partecipanti, ore totali, media ore per partecipante.</p>
    `
  },
  {
    id: 3,
    title: "Import Massivo Soci e Volontari",
    icon: FileUp,
    content: `
      <p>L'<strong>Import Massivo</strong> permette di importare soci e volontari da file esterni con un wizard guidato a 4 step.</p>
      
      <div class="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
        <p class="font-semibold text-purple-800">Limite Piano Pro: max 500 soggetti per import</p>
        <p class="text-purple-700 text-sm">Il piano Enterprise ha import illimitato.</p>
      </div>
      
      <h4>Formati supportati</h4>
      <ul>
        <li><strong>CSV</strong> - separatore ; o , (auto-rilevato)</li>
        <li><strong>Excel</strong> - .xlsx, .xls</li>
        <li><strong>OpenDocument</strong> - .ods (LibreOffice Calc)</li>
        <li><strong>Formato Federazioni Nazionali</strong> - riconoscimento automatico file con 50+ colonne</li>
      </ul>
      
      <h4>Procedura di Import (4 Step)</h4>
      <ol>
        <li><strong>Step 1 - Carica File:</strong> Trascina o seleziona il file</li>
        <li><strong>Step 2 - Mappa Campi:</strong> Verifica la mappatura automatica o personalizzala</li>
        <li><strong>Step 3 - Anteprima:</strong> Controlla record validi, duplicati ed errori</li>
        <li><strong>Step 4 - Risultato:</strong> Visualizza il riepilogo dell'import</li>
      </ol>
      
      <h4>Mappatura Automatica Intelligente</h4>
      <p>Il sistema riconosce automaticamente 27+ colonne comuni:</p>
      <ul>
        <li>Cognome, Nome, Codice Fiscale</li>
        <li>Numero Tessera, Data Iscrizione, Tipo Socio</li>
        <li>Email, Telefono, Cellulare</li>
        <li>Data/Luogo di nascita, Sesso</li>
        <li>Indirizzo, CAP, Citta, Provincia, Regione</li>
        <li>"Il socio e anche Volontario" (crea doppio ruolo)</li>
      </ul>
      
      <h4>Tipo di Import</h4>
      <ul>
        <li><strong>Soci</strong> - Importa come soci con numero tessera</li>
        <li><strong>Volontari</strong> - Importa come volontari</li>
        <li><strong>Entrambi</strong> - Crea sia ruolo socio che volontario</li>
      </ul>
      
      <h4>Gestione Duplicati</h4>
      <p>Se un codice fiscale esiste gia:</p>
      <ul>
        <li><strong>Salta</strong> - Non importa il record duplicato</li>
        <li><strong>Aggiorna</strong> - Sovrascrive i dati esistenti</li>
      </ul>
      
      <h4>Validazioni Automatiche</h4>
      <ul>
        <li>Codice fiscale italiano (16 caratteri)</li>
        <li>Formato email</li>
        <li>Date in vari formati (DD/MM/YYYY, YYYY-MM-DD, ecc.)</li>
        <li>Stato normalizzato (attivo/sospeso/cessato)</li>
      </ul>
      
      <h4>Template Scaricabili</h4>
      <p>Dalla pagina Import puoi scaricare template CSV vuoti per Soci e Volontari con tutte le colonne predefinite.</p>
    `
  },
  {
    id: 4,
    title: "Certificati Pro",
    icon: Award,
    content: `
      <p>Il Piano Pro sblocca template di certificati aggiuntivi.</p>
      
      <h4>Template Premium (inclusi):</h4>
      <ul>
        <li>Attestato di Partecipazione</li>
        <li>Certificato Ore Volontariato</li>
      </ul>
      
      <h4>Template Pro (esclusivi):</h4>
      <ul>
        <li><strong>Attestato di Merito</strong> - Riconoscimento speciale per volontari meritevoli. Puoi personalizzare la motivazione del premio.</li>
        <li><strong>Attestato di Formazione</strong> - Certifica la partecipazione a corsi ed eventi formativi. Puoi specificare il nome del corso completato.</li>
      </ul>
      
      <h4>Personalizzazione</h4>
      <p>Per l'Attestato di Merito puoi inserire una motivazione personalizzata, ad esempio:</p>
      <ul>
        <li>"per l'eccezionale dedizione dimostrata nelle attività di assistenza"</li>
        <li>"per il prezioso contributo durante l'emergenza..."</li>
      </ul>
      
      <p>Per l'Attestato di Formazione puoi specificare il nome del corso, ad esempio:</p>
      <ul>
        <li>"Corso base primo soccorso"</li>
        <li>"Formazione sulla sicurezza"</li>
      </ul>
    `
  },
  {
    id: 5,
    title: "Log Operazioni (Audit Trail)",
    icon: Shield,
    content: `
      <p>Il Log Operazioni registra tutte le azioni effettuate sulla piattaforma.</p>
      
      <h4>Informazioni registrate:</h4>
      <ul>
        <li>Data e ora dell'operazione</li>
        <li>Utente che ha eseguito l'azione</li>
        <li>Tipo di operazione (creazione, modifica, eliminazione)</li>
        <li>Sezione interessata (volontari, eventi, documenti, ecc.)</li>
        <li>Dettagli dell'operazione</li>
      </ul>
      
      <h4>Filtri disponibili:</h4>
      <ul>
        <li>Per sezione</li>
        <li>Per esito (successo/errore)</li>
        <li>Per periodo</li>
        <li>Per utente</li>
      </ul>
      
      <h4>Utilità</h4>
      <p>Il log è utile per:</p>
      <ul>
        <li>Tracciare chi ha modificato cosa e quando</li>
        <li>Verificare la conformità alle procedure</li>
        <li>Risolvere problemi e contestazioni</li>
        <li>Audit interni ed esterni</li>
      </ul>
    `
  },
  {
    id: 6,
    title: "FAQ Piano Pro",
    icon: HelpCircle,
    content: `
      <h4>Quanto costa il Piano Pro?</h4>
      <p>Il Piano Pro costa €24,99/mese (IVA inclusa).</p>
      
      <h4>Quanti volontari posso gestire?</h4>
      <p>Con il piano Pro puoi gestire fino a <strong>250 volontari attivi</strong> e <strong>500 soggetti unici</strong>.</p>
      
      <h4>Quanto spazio ho per i documenti?</h4>
      <p>Il piano Pro include 5 GB di storage per i documenti.</p>
      
      <h4>Cosa include in più rispetto al Governance?</h4>
      <p>Rendicontazione completa (120+ categorie), Statistiche avanzate, <strong>Gestione Magazzino e Assegnazioni</strong>, <strong>Gestione Automezzi</strong> (assicurazioni, revisioni, manutenzioni), Import massivo (max 500), Chatbot AI, batch certificati illimitati, Schemi Ministeriali completi (MOD. A, B), supporto prioritario.</p>
      
      <h4>Cosa include il Magazzino?</h4>
      <p>Inventario articoli (radio, divise, antenne, PC, ecc.) con categorie, numeri seriali, taglie. Assegnazione materiale a volontari con traccia delle consegne e restituzioni.</p>
      
      <h4>Cosa include gli Automezzi?</h4>
      <p>Gestione veicoli con targa, assicurazioni (con frazionamento e rate), revisioni, manutenzioni. Alert scadenze automatici integrati nello Scadenzario generale.</p>
      
      <h4>Posso importare volontari da Excel?</h4>
      <p>Si, puoi importare fino a 500 soggetti alla volta da file CSV, Excel (.xlsx) o ODS. Con possibilità di Rollback.</p>
      
      <h4>Qual è la differenza con Enterprise?</h4>
      <p>Il piano Enterprise (€49,99/mese) offre: volontari/soggetti illimitati, 20GB storage, supporto prioritario via email (supporto@assietica.it).</p>
    `
  }
];

// Componente per renderizzare una sezione del manuale
const ManualSection = ({ chapters, planColor, planName }) => {
  const [openChapter, setOpenChapter] = useState(1);

  const toggleChapter = (id) => {
    setOpenChapter(openChapter === id ? null : id);
  };

  return (
    <div className="space-y-4">
      {/* Quick Navigation */}
      <Card className="bg-slate-50">
        <CardHeader>
          <CardTitle className="text-lg">Indice - {planName}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-2">
            {chapters.map((chapter) => (
              <button
                key={chapter.id}
                onClick={() => {
                  setOpenChapter(chapter.id);
                }}
                className="flex items-center gap-3 p-3 text-left rounded-lg hover:bg-white transition-colors group"
              >
                <span className={`flex items-center justify-center w-8 h-8 text-white rounded-full text-sm font-bold`} style={{ backgroundColor: planColor }}>
                  {chapter.id}
                </span>
                <span className="text-sm text-slate-700 group-hover:text-slate-900">{chapter.title}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Chapters */}
      <div className="space-y-4">
        {chapters.map((chapter) => {
          const IconComponent = chapter.icon;
          const isOpen = openChapter === chapter.id;
          
          return (
            <Card 
              key={chapter.id} 
              className={`transition-all ${isOpen ? 'ring-2' : ''}`}
              style={{ borderColor: isOpen ? planColor : undefined }}
            >
              <CardHeader 
                className="cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleChapter(chapter.id)}
              >
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center justify-center w-10 h-10 text-white rounded-full font-bold" style={{ backgroundColor: planColor }}>
                      {chapter.id}
                    </span>
                    <div className="flex items-center gap-3">
                      <IconComponent className="w-5 h-5" style={{ color: planColor }} />
                      <span className="text-lg">{chapter.title}</span>
                    </div>
                  </div>
                  {isOpen ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </CardTitle>
              </CardHeader>
              
              {isOpen && (
                <CardContent className="pt-0">
                  <div 
                    className="prose prose-slate max-w-none
                      prose-headings:text-slate-800 prose-headings:font-semibold
                      prose-h4:text-base prose-h4:mt-6 prose-h4:mb-3
                      prose-p:text-slate-600 prose-p:leading-relaxed
                      prose-ul:text-slate-600 prose-ul:my-3
                      prose-ol:text-slate-600 prose-ol:my-3
                      prose-li:my-1
                      prose-strong:text-slate-800"
                    dangerouslySetInnerHTML={{ __html: chapter.content }}
                  />
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default function ManualeUso() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="space-y-6" data-testid="manuale-uso-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0055BB]/10 rounded-full mb-4">
          <BookOpen className="w-8 h-8 text-[#0055BB]" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Manuale d'Uso
        </h1>
        <p className="text-slate-500 mt-2 max-w-2xl mx-auto">
          Guida completa per l'utilizzo della piattaforma Assietica Gest <span className="italic">by Assietica</span>.<br />
          Seleziona il tuo piano per visualizzare le funzionalità disponibili.
        </p>
      </div>

      {/* Tabs per Piano */}
      <Tabs defaultValue="gratuito" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="gratuito" className="data-[state=active]:bg-slate-600 data-[state=active]:text-white">
            <BookOpen className="w-4 h-4 mr-2" />
            Gratuito
          </TabsTrigger>
          <TabsTrigger value="premium" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
            <Crown className="w-4 h-4 mr-2" />
            Premium
          </TabsTrigger>
          <TabsTrigger value="governance" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Landmark className="w-4 h-4 mr-2" />
            Governance
          </TabsTrigger>
          <TabsTrigger value="pro" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
            <Star className="w-4 h-4 mr-2" />
            Pro
          </TabsTrigger>
        </TabsList>

        {/* Piano Gratuito */}
        <TabsContent value="gratuito">
          <Card className="mb-6 bg-slate-50 border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-slate-600 rounded-full">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Piano Gratuito</h2>
                  <p className="text-slate-600">Funzionalità essenziali per iniziare a gestire la tua associazione</p>
                </div>
                <Badge className="ml-auto bg-slate-600">GRATIS</Badge>
              </div>
            </CardContent>
          </Card>
          <ManualSection chapters={chaptersGratuito} planColor="#475569" planName="Piano Gratuito" />
        </TabsContent>

        {/* Piano Premium */}
        <TabsContent value="premium">
          <Card className="mb-6 bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-500 rounded-full">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-amber-800">Piano Premium</h2>
                  <p className="text-amber-700">Funzionalità avanzate per una gestione completa</p>
                </div>
                <Badge className="ml-auto bg-amber-500">€9,99/mese (IVA incl.)</Badge>
              </div>
            </CardContent>
          </Card>
          <ManualSection chapters={chaptersPremium} planColor="#F59E0B" planName="Piano Premium" />
        </TabsContent>

        {/* Piano Governance */}
        <TabsContent value="governance">
          <Card className="mb-6 bg-indigo-50 border-indigo-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-600 rounded-full">
                  <Landmark className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-indigo-800">Piano Governance</h2>
                  <p className="text-indigo-700">Governance avanzata e rendicontazione economica</p>
                </div>
                <Badge className="ml-auto bg-indigo-600">€14,99/mese (IVA incl.)</Badge>
              </div>
            </CardContent>
          </Card>
          <ManualSection chapters={chaptersGovernance} planColor="#4F46E5" planName="Piano Governance" />
        </TabsContent>

        {/* Piano Pro */}
        <TabsContent value="pro">
          <Card className="mb-6 bg-purple-50 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-600 rounded-full">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-purple-800">Piano Pro</h2>
                  <p className="text-purple-700">Tutte le funzionalità per enti con esigenze avanzate</p>
                </div>
                <Badge className="ml-auto bg-purple-600">€24,99/mese (IVA incl.)</Badge>
              </div>
            </CardContent>
          </Card>
          <ManualSection chapters={chaptersPro} planColor="#9333EA" planName="Piano Pro" />
        </TabsContent>
      </Tabs>

      {/* Conclusion */}
      <Card className="bg-gradient-to-br from-[#0055BB] to-[#003d88] text-white">
        <CardContent className="py-8 text-center">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h3 className="text-xl font-bold mb-2">Hai bisogno di aiuto?</h3>
          <p className="opacity-90 max-w-2xl mx-auto">
            Se hai domande o necessiti di assistenza, contattaci. Il nostro team è a disposizione per aiutarti.
          </p>
          <p className="opacity-80 mt-4 text-sm">
            <strong>supporto@assietica.it</strong>
          </p>
        </CardContent>
      </Card>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 rounded-full w-12 h-12 shadow-lg"
          size="icon"
        >
          <ArrowUp className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
}
